import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import {
  formatDateWithTime,
  isReadOnlyUser,
  withRouter,
} from "../../shared/utility";
import { BarcodeHistoryViewModel } from "../../viewmodels/traceability/BarcodeHistoryViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BarcodeHistoryTable from "./barcodeHistory/BarcodeHistoryTable";
import EmptyModal from "../../components/modals/EmptyModal";
import { Skeleton } from "primereact/skeleton";
import ButtonSpinner from "../../components/controls/ButtonSpinner";
import CopyableIdentifier from "../../components/controls/CopyableIdentifier";
import HistoryRecordSummary, { HistoryDetail } from "../../components/traceability/HistoryRecordSummary";

export function BarcodeHistoryScreen(props) {
  const { logout } = props;
  const {
    product,
    getBarcodeHistory,
    loading,
    emptyModal,
    setEmptyModelOpen,
    askDeleteManufacturedItemByBarcode,
  } = BarcodeHistoryViewModel(logout);

  const [searchParams] = useSearchParams();
  const requestedBarcode = searchParams.get("barcode")?.trim() ?? "";
  const [barcodeInput, setBarcodeInput] = useState(requestedBarcode);
  const isReadOnly = isReadOnlyUser();

  useEffect(() => {
    setBarcodeInput(requestedBarcode);
    if (requestedBarcode) getBarcodeHistory(requestedBarcode);
  }, [getBarcodeHistory, requestedBarcode]);

  return (
    <div>
      <div className="mt-2 space-y-4 px-4 lg:px-8">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 sm:px-5">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold leading-7 text-slate-800">
              Item history
            </h1>
            <label htmlFor="searchProduct" className="mb-1.5 mt-4 block text-sm font-medium text-slate-700">Barcode</label>
            <div className="flex max-w-2xl rounded-md">
              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <input
                  type="search"
                  name="searchProduct"
                  id="searchProduct"
                  className="block min-h-[44px] w-full min-w-0 rounded-l-md border border-slate-300 px-3 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:min-h-[40px]"
                  placeholder="Search by barcode"
                  value={barcodeInput}
                  onChange={(event) => setBarcodeInput(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      getBarcodeHistory(barcodeInput);
                  }}
                />
              </div>
              <button
                type="button"
                aria-label="Search history"
                className="-ml-px inline-flex min-w-[44px] items-center justify-center rounded-r-md border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                onClick={() => getBarcodeHistory(barcodeInput)}
              >
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="space-y-2">
            <Skeleton width="100%" height="8rem" borderRadius="16px"></Skeleton>
            <Skeleton width="100%" height="8rem" borderRadius="16px"></Skeleton>
          </div>
        ) : product.serial ? (
          <div>
            <HistoryRecordSummary
              title={product.name || "Item details"}
              status={product.statusReason}
              barcode={product.barcode}
              actions={
                !isReadOnly && product.itemType === "manufactured" ? (
                    <button
                      type="button"
                      className="inline-flex min-h-[44px] items-center rounded-md border border-red-200 bg-white px-3 text-sm font-medium text-red-600 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 sm:min-h-[36px]"
                      onClick={askDeleteManufacturedItemByBarcode}
                    >
                      <ButtonSpinner spin={false} text="Delete" />
                    </button>
                ) : null
              }
            >
              <HistoryDetail label="Serial #">
                <CopyableIdentifier value={product.barcode} displayValue={product.serial} description="full barcode" />
              </HistoryDetail>
              <HistoryDetail label="PLU">{product.plu}</HistoryDetail>
              <HistoryDetail label="Name (CH)">{product.name_ch}</HistoryDetail>
              <HistoryDetail label="Lot #">
                <CopyableIdentifier value={product.lot_number} description="lot number" />
              </HistoryDetail>
              <HistoryDetail label="Location">{product.warehouse_location_name}</HistoryDetail>
              <HistoryDetail label="Last scan date">{formatDateWithTime(product.lastScanDate)}</HistoryDetail>
              <HistoryDetail label="On hand weight">{product.on_hand_net_kg == null ? "—" : `${product.on_hand_net_kg} kg`}</HistoryDetail>
              <HistoryDetail label="On hand pieces">{product.on_hand_piece_count}</HistoryDetail>
              <HistoryDetail label="Total cartons">{product.total_cartons}</HistoryDetail>
              <HistoryDetail label="Inventory by PLU">{product.inv_on_hand_by_plu}</HistoryDetail>
              <HistoryDetail label="Inventory by lot">{product.inv_on_hand_by_lot}</HistoryDetail>
            </HistoryRecordSummary>
            <BarcodeHistoryTable
              historyData={product.barcode_histories}
              sellingUnitOfMeasure={product.selling_unit_of_measure}
            />
          </div>
        ) : null}
      </div>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
        size={emptyModal.size}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(BarcodeHistoryScreen)
);
